import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from 'lib/httpClient';
import { ApiResponse, SuccessResponse } from 'further-types/api';
import { Api } from 'further-types/firm';

export function useListFirmBankDetails(
  firmId?: string,
  refetchOnWindowFocus?: boolean,
) {
  return useQuery({
    queryKey: ['firm-bank-details', firmId],
    queryFn: async () =>
      (
        await api.get<ApiResponse<Api.FirmBankDetailsResponse>>(
          `/firm/${firmId}/bank-details`,
        )
      ).data.data,
    enabled: !!firmId,
    placeholderData: keepPreviousData,
    staleTime: refetchOnWindowFocus ? 0 : 10 * 60 * 1000,
    refetchOnWindowFocus: refetchOnWindowFocus ? 'always' : false,
  });
}

export function useAddFirmBankDetails(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, unknown, Api.CreateFirmBankDetailBody>({
    mutationFn: async (data) => {
      const { data: result } = await api.post<ApiResponse<SuccessResponse>>(
        `/firm/${firmId}/bank-details`,
        data,
      );

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['firm-bank-details', firmId],
      });
    },
  });
}

export function useEditFirmBankDetails(firmId: string, bankDetailId: string) {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, unknown, Api.CreateFirmBankDetailBody>({
    mutationFn: async (data) => {
      const { data: result } = await api.put<ApiResponse<SuccessResponse>>(
        `/firm/${firmId}/bank-details/${bankDetailId}`,
        data,
      );

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['firm-bank-details', firmId],
      });
    },
  });
}

export function useDeleteFirmBankDetails(firmId: string) {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, unknown, { bankDetailId: string }>({
    mutationFn: async (data) => {
      const { data: result } = await api.delete<ApiResponse<SuccessResponse>>(
        `/firm/${firmId}/bank-details/${data.bankDetailId}`,
      );

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['firm-bank-details', firmId],
      });
    },
  });
}
